import React, { Fragment, useEffect, useRef, useState } from "react";
import { Trophy, RotateCw } from "lucide-react";
import MembershipStatsCard from "../components/ui/MembershipStatsCard";
import RankingItem from "../components/ui/RankingItem";
import MemberChartModal from "../components/modals/MemberChartModal";
import { useRankings, useRefresh } from "../hooks";
import { useMypageContext } from "../context/MypageContext";
import { RankingUser } from "../types";

const STALE_MS = 5 * 60 * 1000;

const RankingView: React.FC = () => {
  const {
    rankings,
    isLoading,
    error,
    fetchAll,
    lastFetchedAt: rankingsLastFetchedAt,
  } = useRankings();
  const {
    mypage,
    refresh,
    lastFetchedAt: mypageLastFetchedAt,
  } = useMypageContext();
  const [selectedUser, setSelectedUser] = useState<RankingUser | null>(null);
  const { isRefreshing, refreshImmediate } = useRefresh({
    showErrorToast: true,
    showSuccessToast: false,
  });

  const fetchAllRef = useRef(fetchAll);
  fetchAllRef.current = fetchAll;
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;
  const rankingsLastFetchedAtRef = useRef(rankingsLastFetchedAt);
  rankingsLastFetchedAtRef.current = rankingsLastFetchedAt;
  const mypageLastFetchedAtRef = useRef(mypageLastFetchedAt);
  mypageLastFetchedAtRef.current = mypageLastFetchedAt;

  useEffect(() => {
    const now = Date.now();
    if (!rankingsLastFetchedAt || now - rankingsLastFetchedAt > STALE_MS)
      fetchAll();
    if (!mypageLastFetchedAt || now - mypageLastFetchedAt > STALE_MS) refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState !== "visible") return;
      const now = Date.now();
      if (
        !rankingsLastFetchedAtRef.current ||
        now - rankingsLastFetchedAtRef.current > STALE_MS
      ) {
        fetchAllRef.current();
      }
      if (
        !mypageLastFetchedAtRef.current ||
        now - mypageLastFetchedAtRef.current > STALE_MS
      ) {
        refreshRef.current();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  const handleRefresh = async () => {
    await refreshImmediate(fetchAll, refresh);
  };

  return (
    <div className="flex flex-col h-full animate-in fade-in duration-300 overflow-hidden">
      <div className="overflow-y-auto scrollbar-hide flex-1">
        <div className="flex items-center justify-between pt-[calc(env(safe-area-inset-top)+0.75rem)] pb-3 px-6 sticky top-0 bg-[#f8fafc]/80 backdrop-blur-xl z-20 border-b border-slate-200/50 shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-md bg-gradient-to-tr from-blue-500 to-cyan-500 flex items-center justify-center shadow-md rotate-3">
              <Trophy
                className="w-[18px] h-[18px] text-white -rotate-3"
                strokeWidth={2.5}
              />
            </div>
            <h1 className="text-xl font-black tracking-tighter uppercase bg-gradient-to-r from-blue-500 to-cyan-500 bg-clip-text text-transparent">
              ランキング
            </h1>
          </div>
          <button
            onClick={handleRefresh}
            disabled={isRefreshing || isLoading}
            className="group inline-flex h-9 w-9 items-center justify-center rounded-lg hover:bg-slate-100 active:bg-slate-200 active:scale-95 active:shadow-inner transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
            title="データを更新"
          >
            <RotateCw
              className={`block w-5 h-5 text-slate-600 transition-transform duration-150 group-active:scale-90 group-active:-rotate-12 ${isRefreshing || isLoading ? "animate-spin" : ""}`}
              strokeWidth={2}
            />
          </button>
        </div>
        <div className="px-6 py-6 pb-40 text-left">
          <MembershipStatsCard
            points={mypage?.yearly_point ?? "-"}
            rank={mypage?.yearly_rank ?? "-"}
            dartsliveName={mypage?.dartslive_card_name}
            eventGroupName={mypage?.event_group?.name}
            theme="blue"
          />
          <div className="mb-5 pl-1 flex items-center gap-2">
            <h2 className="text-[11px] font-black text-slate-400 uppercase tracking-widest text-left">
              年間ランキング
            </h2>
            {mypage?.event_group?.name && (
              <span className="text-[10px] font-bold text-blue-600 bg-blue-50 border border-blue-100 rounded-full px-2 py-0.5">
                {mypage.event_group.name}
              </span>
            )}
          </div>
          {(isLoading || isRefreshing) && (
            <p className="text-slate-400 text-xs font-bold text-center py-8">
              読み込み中...
            </p>
          )}
          {error && (
            <p className="text-rose-500 text-xs font-bold text-center py-8">
              {error}
            </p>
          )}
          <div className="space-y-1 text-left">
            {rankings.map((user, index) => {
              const nextUser = rankings[index + 1];
              const shouldShowTop8Divider =
                user.rank <= 8 &&
                (nextUser?.rank ?? Number.MAX_SAFE_INTEGER) >= 9;

              return (
                <Fragment key={user.rank}>
                  <RankingItem
                    {...user}
                    onClick={() => setSelectedUser(user)}
                  />
                  {shouldShowTop8Divider && (
                    <div
                      aria-hidden="true"
                      className="my-2 border-t-2 border-dashed border-cyan-300"
                    />
                  )}
                </Fragment>
              );
            })}
          </div>
        </div>
      </div>

      <MemberChartModal
        isOpen={selectedUser !== null}
        onClose={() => setSelectedUser(null)}
        selectedUser={selectedUser}
        rankings={rankings}
      />
    </div>
  );
};

export default RankingView;
